import { HStack } from '@astryxdesign/core/HStack';
import { IconButton } from '@astryxdesign/core/IconButton';
import { useToast } from '@astryxdesign/core/Toast';
import { VStack } from '@astryxdesign/core/VStack';
import { ArrowLeft, X } from '@phosphor-icons/react';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router';
import { useCreateBooking } from '@/api/public';
import type { Studio } from '@/api/schemas';
import { Drawer, DrawerContent, DrawerDescription, DrawerTitle } from '@/components/ui/drawer';
import { EmptyView } from '@/components/StateViews';
import { errorCode, errorText } from '@/lib/errors';
import { clearContactDraft, ContactsStep, type ContactForm } from './ContactsStep';
import { ServiceStep } from './ServiceStep';
import { TimeStep } from './TimeStep';
import { useBookingFlow } from './useBookingFlow';
import { studioUrl } from '@shared/routes';

const TITLES = { service: 'Выберите услугу', time: 'Дата и время', contacts: 'Контакты' } as const;
const STEP_NO = { service: 1, time: 2, contacts: 3 } as const;

/** One sequential bottom sheet: service → date/time → contacts → confirm. */
export function BookingSheet({ studio }: { studio: Studio }) {
  const flow = useBookingFlow();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const showToast = useToast();
  const create = useCreateBooking(studio.slug);
  const headingRef = useRef<HTMLHeadingElement | null>(null);

  const service = studio.services.find((s) => s.id === flow.serviceId);
  const step = flow.step === 'contacts' && !flow.time ? 'time' : flow.step === 'time' && !service ? 'service' : flow.step;

  // Move focus to the step title so screen readers announce each step.
  useEffect(() => {
    if (flow.isOpen) requestAnimationFrame(() => headingRef.current?.focus());
  }, [flow.isOpen, step]);

  const submit = (form: ContactForm) => {
    if (!service || !flow.time) return;
    create.mutate(
      { serviceId: service.id, startsAt: flow.time, ...form },
      {
        onSuccess: ({ booking }) => {
          clearContactDraft();
          create.reset();
          navigate(`${studioUrl.booking(studio.slug, booking.id)}&created=1`, { replace: true, state: null });
        },
        onError: (err) => {
          if (errorCode(err) === 'slot_taken' || errorCode(err) === 'slot_not_offered') {
            showToast({ body: errorText(err), type: 'error', uniqueID: 'slot-taken' });
            void qc.invalidateQueries({ queryKey: ['public', 'availability', studio.slug] });
            flow.toTime();
          }
        },
      },
    );
  };

  return (
    <Drawer open={flow.isOpen} onOpenChange={(open) => !open && flow.close()}>
      <DrawerContent className="booking-sheet">
        <VStack gap={0} height="100%">
          <HStack gap={2} vAlign="center" paddingInline={4} paddingBlock={3}>
            {step !== 'service' && flow.canGoBack ? (
              <IconButton label="Назад" icon={<ArrowLeft size={20} />} variant="ghost" onClick={flow.back} />
            ) : (
              <span className="sheet-spacer" aria-hidden />
            )}
            <VStack gap={0} hAlign="center" className="sheet-title">
              <DrawerTitle ref={headingRef} tabIndex={-1} className="text-lg font-semibold outline-none">
                {TITLES[step]}
              </DrawerTitle>
              <DrawerDescription>Шаг {STEP_NO[step]} из 3 · {studio.settings.shortName}</DrawerDescription>
            </VStack>
            <IconButton label="Закрыть" icon={<X size={20} />} variant="ghost" onClick={flow.close} />
          </HStack>

          <VStack gap={4} paddingInline={4} paddingBlockEnd={6} isScrollable className="sheet-body">
            {step === 'service' && <ServiceStep studio={studio} onChoose={flow.chooseService} />}
            {step === 'time' && service && (
              <TimeStep studio={studio} service={service} date={flow.date} onDate={flow.chooseDate} onTime={flow.chooseTime} />
            )}
            {step === 'contacts' && service && flow.time && (
              <ContactsStep
                studio={studio}
                service={service}
                startsAt={flow.time}
                submitting={create.isPending}
                error={create.error}
                onSubmit={submit}
              />
            )}
            {!service && flow.serviceId && <EmptyView title="Услуга недоступна" description="Выберите другую услугу." />}
          </VStack>
        </VStack>
      </DrawerContent>
    </Drawer>
  );
}
